import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { useApiStore } from "@/stores/api/index.ts";
import dayjs from "dayjs";
import { BLANK_DATES } from "@/v2/shared/constants.js";
import { entryDefaults } from "@/util/constants.ts";
import { EntryDocument } from "@/types/entry.ts";
import {
  CategoryFilters,
  PartialCategoryDocument,
  CategoryDocument,
  RecordColumn,
} from "@/types/category.ts";
import { MediaItem, MediaType } from "@/types/media.ts";
import { SiteDocument } from "@/types/site.ts";
import mapDocToSite from "@/components/HomeLayouts/mapDocToSite.ts";
import { PennantProps } from "@/v2/components/ProfileCard/Pennant/Pennant.types.ts";
import { getHostname } from "@/util/hostnames.ts";
import { transformMedia } from "@/util/mediaHelper.v2.ts";
import { categoryDocumentToRecordColumns } from "@/components/CategoryLayouts/categoryDocumentToRecordColumns.js";
import type {
  CastScreen,
  SlideType,
  SlideItem,
} from "@/stores/cast-screen/types";

const SCREEN_DATA_REFRESH_INTERVAL = 10800000;
const STYLING_DATA_REFRESH_INTERVAL = 10000;
const DEFAULT_SLIDE_INTERVAL = 5000;
const TITLE_INTERVAL_OFFSET = 1000;

const PLACEHOLDER_TEXT = [
  "No media to display",
  "Please contact a site admin to add media to this screen.",
];

function getFormattedDate(date: string): string {
  const dateObj = dayjs(date);
  return dateObj.isValid() ? dateObj.format("YYYY") : "";
}

function getPersonName(entry: EntryDocument | null): string {
  return `${entry?.firstName ?? ""} ${entry?.lastName ?? ""}`.trim();
}

function isValidEntry(entry: EntryDocument): boolean {
  return entry.isLive && entry.firstName !== entryDefaults.firstName;
}

function transformEntryMedia(entry: EntryDocument | null): MediaItem | null {
  if (!entry) return null;

  try {
    return transformMedia(entry?.headshot?.[0] ?? entry?.media?.[0] ?? null);
  } catch {
    return null;
  }
}

function generateAttributeArray(
  entry: EntryDocument | null,
  category: CategoryDocument | null
): { name: string; value: string }[] {
  if (!category || !entry) return [];

  const {
    eventDateAlternativeName,
    graduationDateAlternativeName,
    activityAlternativeName,
    typeAlternativeName,
    attributesCategories,
  } = category;

  const labelMap = {
    eventDate: eventDateAlternativeName?.trim() || "Date",
    graduationDate: graduationDateAlternativeName?.trim() || "Graduation",
    activity: activityAlternativeName?.trim() || "Sport",
    type: typeAlternativeName?.trim() || "Type",
  };

  Object.keys(attributesCategories).forEach((attr) => {
    if (!attributesCategories[attr as keyof CategoryFilters]) {
      delete labelMap[attr as keyof typeof labelMap];
    }
  });

  return Object.entries(labelMap)
    .map(([key, label]) => {
      const value = (
        entry[key as keyof EntryDocument] as string | undefined
      )?.trim();

      if (!value || BLANK_DATES.includes(value)) return null;

      const formattedValue = key.toLowerCase().includes("date")
        ? getFormattedDate(value)
        : value;

      return { name: label, value: formattedValue };
    })
    .filter(Boolean) as { name: string; value: string }[];
}

function createSlideItem(
  type: SlideType,
  entry: EntryDocument | null,
  category: CategoryDocument | null,
  recordColumnProps: RecordColumn | null = null,
  pennantProps: PennantProps | null = null
): SlideItem {
  const media = transformEntryMedia(entry);
  let defaultPersonCardBackground;
  try {
    defaultPersonCardBackground = category?.defaultPersonCardBackground?.[0]
      ? transformMedia(category.defaultPersonCardBackground[0])
      : null;
  } catch {
    defaultPersonCardBackground = null;
  }

  return {
    type,
    personName: getPersonName(entry),
    attributes: generateAttributeArray(entry, category),
    mediaUrl: media?.url ?? null,
    mediaType: (media?.type as MediaType) ?? "image",
    categoryName: category?.categoryName ?? "",
    recordColumnProps,
    pennantProps,
    defaultPersonCardBackground: defaultPersonCardBackground?.url ?? null,
  };
}

export const useCastScreenStore = defineStore("cast-screen", () => {
  const api = useApiStore();

  const isInitialized = ref(false);
  const castScreen = ref<CastScreen | null>(null);

  const slides = ref<SlideItem[]>([]);

  const currentSlideIndex = ref(0);
  const currentTitleIndex = ref(0);

  const slideAdvanceInterval = ref<ReturnType<typeof setInterval>>();
  const titleAdvanceTimeout = ref<ReturnType<typeof setTimeout>>();

  const screenDataRefreshInterval = ref<ReturnType<typeof setTimeout>>();
  const stylingDataRefreshInterval = ref<ReturnType<typeof setTimeout>>();

  const logoUrl = computed(() =>
    castScreen.value?.config?.showLogo &&
    castScreen.value?.stylingData?.primaryLogo?.[0]
      ? transformMedia(castScreen.value?.stylingData?.primaryLogo?.[0])?.url ??
        null
      : null
  );

  const showQRCode = computed(() => castScreen.value?.config?.showQRCode);

  const categoryName = computed(() =>
    castScreen.value?.config?.showTitle
      ? slides.value?.[currentTitleIndex.value]?.categoryName ?? ""
      : null
  );

  const emergencyAnnouncement = computed(() => {
    if (
      castScreen.value?.stylingData?.castEmergencyAnnouncementEnabled &&
      castScreen.value?.config?.enableEmergencyAnnouncement
    ) {
      return castScreen.value.stylingData.castEmergencyAnnouncementText;
    }

    return null;
  });

  const emergencyAnnouncementTheme = computed(() => {
    if (castScreen.value?.stylingData?.castEmergencyAnnouncementTheme) {
      return castScreen.value.stylingData.castEmergencyAnnouncementTheme;
    }

    return "critical";
  });

  const genericImage = computed(() => {
    try {
      if (!castScreen.value?.stylingData?.genericImage?.[0]) {
        return null;
      }

      return transformMedia(castScreen.value?.stylingData?.genericImage[0]).url;
    } catch (e) {
      return null;
    }
  });

  const backgroundImageUrl = computed(() => {
    try {
      if (!castScreen.value?.stylingData?.backgroundImage?.[0]) {
        return null;
      }

      return transformMedia(castScreen.value?.stylingData?.backgroundImage[0])
        .url;
    } catch (e) {
      return null;
    }
  });

  const accentColor = computed(
    () => castScreen.value?.stylingData?.primaryColor ?? "#f3f3f3"
  );

  const pathUrl = computed(() =>
    castScreen.value?.siteID
      ? `https://${getHostname()}/home/${castScreen.value.siteID}`
      : ""
  );

  const presentingSponsorImages = computed(() => {
    if (castScreen.value?.config?.showPresentingSponsors) {
      return castScreen.value?.stylingData?.presentingSponsorImages;
    }

    return null;
  });

  function clearNetworkIntervals(): void {
    [screenDataRefreshInterval.value, stylingDataRefreshInterval.value].forEach(
      (interval) => {
        if (interval) {
          clearInterval(interval);
        }
      }
    );
  }

  function resetSlideshowInterval(): void {
    if (slideAdvanceInterval.value) {
      clearInterval(slideAdvanceInterval.value);
    }

    if (titleAdvanceTimeout.value) {
      clearTimeout(titleAdvanceTimeout.value);
    }

    currentSlideIndex.value = 0;
    currentTitleIndex.value = 0;

    const timePerSlideInMs = castScreen.value?.config?.timePerSlideInMs;

    const isTimePerSlideInMsDefined =
      timePerSlideInMs !== undefined &&
      timePerSlideInMs !== null &&
      !isNaN(Number(timePerSlideInMs));

    const slideIntervalMs = isTimePerSlideInMsDefined
      ? Math.max(0, Number(timePerSlideInMs))
      : DEFAULT_SLIDE_INTERVAL;

    slideAdvanceInterval.value = setInterval(() => {
      currentSlideIndex.value =
        currentSlideIndex.value + 1 < (slides.value.length ?? 0)
          ? currentSlideIndex.value + 1
          : 0;

      if (titleAdvanceTimeout.value) {
        clearTimeout(titleAdvanceTimeout.value);
      }

      titleAdvanceTimeout.value = setTimeout(() => {
        currentTitleIndex.value = currentSlideIndex.value;
      }, TITLE_INTERVAL_OFFSET);
    }, slideIntervalMs);
  }

  async function createSlidesFromCategory(
    categoryData: CategoryDocument,
    category: PartialCategoryDocument
  ): Promise<void> {
    const categoryEntries = categoryData.entries ?? [];
    const categoryRecordColumns = categoryDocumentToRecordColumns(categoryData);

    if (
      category.layout.includes("Record") &&
      categoryRecordColumns.length > 0
    ) {
      categoryRecordColumns.forEach((recordColumn: RecordColumn) => {
        if (recordColumn.records.length === 0) {
          return;
        }

        slides.value.push(
          createSlideItem(
            "recordColumn",
            null,
            { ...categoryData, categoryName: recordColumn.title },
            { ...recordColumn, records: recordColumn.records || [] }
          )
        );
      });

      return;
    }

    if (categoryEntries.length > 0) {
      if (category.layout.includes("PersonBannersLayout")) {
        categoryEntries.filter(isValidEntry).forEach((entry) => {
          slides.value.push(
            createSlideItem(
              "pennant",
              entry,
              { ...categoryData, entries: [] },
              null,
              {
                description: { content: entry.description },
              }
            )
          );
        });

        return;
      }

      categoryEntries.filter(isValidEntry).forEach((entry) => {
        slides.value.push(
          createSlideItem("entry", entry, { ...categoryData, entries: [] })
        );

        if (castScreen.value?.config.showAllProfilePhotos) {
          entry.media.forEach((mediaItem) => {
            slides.value.push(
              createSlideItem(
                "entry",
                { ...entry, headshot: [mediaItem] },
                { ...categoryData, entries: [] }
              )
            );
          });
        }
      });
    }
  }

  async function processCategories(
    categories: PartialCategoryDocument[]
  ): Promise<void> {
    const slidePromises = categories.map(async (category) => {
      const categoryId = category.path.split("/")[2];
      const categoryResponse = await api.fetchCategory(
        categoryId,
        castScreen.value!.clientID
      );

      await createSlidesFromCategory(categoryResponse.data, category);
    });

    await Promise.all(slidePromises);
  }

  async function fetchScreenContent(screenId: string): Promise<void> {
    clearNetworkIntervals();

    screenDataRefreshInterval.value = setInterval(
      () => fetchScreenContent(screenId),
      SCREEN_DATA_REFRESH_INTERVAL
    );

    const {
      data: { clientID, ...rest },
    } = await api.fetchScreen(screenId);
    castScreen.value = { ...rest, clientID };

    const allCategoriesResponse = await api.fetchAllCategories(clientID);
    const filteredCategories = allCategoriesResponse.data.filter(
      (category: PartialCategoryDocument) =>
        castScreen.value?.categoryIds.includes(category.path.split("/")[2])
    );

    if (filteredCategories.length === 0) {
      isInitialized.value = true;
      return;
    }

    await processCategories(filteredCategories);
  }

  async function fetchStylingData(): Promise<void> {
    async function updateStylingData(): Promise<void> {
      if (!castScreen.value) return;

      const stylingDataResponse = await api.fetchStylingData(
        castScreen.value.siteID
      );
      castScreen.value.stylingData = mapDocToSite(
        stylingDataResponse.data
      ) as Partial<SiteDocument>;
    }

    await updateStylingData();

    stylingDataRefreshInterval.value = setInterval(
      updateStylingData,
      STYLING_DATA_REFRESH_INTERVAL
    );
  }

  async function initialize(params: Record<string, string>): Promise<void> {
    await fetchScreenContent(params.screenId as string);
    await fetchStylingData();
    isInitialized.value = true;
    resetSlideshowInterval();
  }

  watch(
    () => [castScreen.value?.config?.timePerSlideInMs, slides.value.length],
    (
      [newTimePerSlideInMs, newSlideCount],
      [oldTimePerSlideInMs, oldSlideCount]
    ) => {
      if (
        newTimePerSlideInMs === oldTimePerSlideInMs &&
        newSlideCount === oldSlideCount
      )
        return;

      resetSlideshowInterval();
    }
  );

  return {
    isInitialized,
    castScreen,
    slides,
    currentSlideIndex,
    slideAdvanceInterval,
    screenDataRefreshInterval,
    stylingDataRefreshInterval,
    logoUrl,
    showQRCode,
    categoryName,
    emergencyAnnouncement,
    emergencyAnnouncementTheme,
    genericImage,
    backgroundImageUrl,
    accentColor,
    pathUrl,
    presentingSponsorImages,
    placeholderText: PLACEHOLDER_TEXT,
    initialize,
    resetSlideshowInterval,
    clearNetworkIntervals,
  };
});
